/**
 * TanStack Query functions for admin-related API calls
 */

import axios from 'axios';
import { buildApiUrl } from '../config/urlConfig.js';
import { queryKeys } from '../queryKeys.js';

/**
 * Search logs with filters
 * @param {Object} filters - Search filters
 * @returns {Promise<Object>} Search results with count and list
 */
export const searchLogs = async (filters = {}) => {
	try {
		// Ensure URL config is initialized before making the request
		const url = buildApiUrl('admin', '/search-logs');
		const response = await axios.post(url, filters, {
			headers: {
				'Content-Type': 'application/json'
			}
		});
		
		// The API returns data directly with { count, list } structure
		if (response.data && response.data.list) {
			return response.data;
		} else {
			throw new Error('Ungültige API-Antwort');
		}
	} catch (error) {
		if (error.response) {
			const errorMessage = error.response.data?.message || `HTTP Error ${error.response.status}`;
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('Netzwerkfehler: Keine Antwort vom Server');
		} else {
			throw new Error(error.message || 'Unbekannter Fehler');
		}
	}
};

/**
 * Get list of rooms for admin purposes
 * @returns {Promise<Array>} List of rooms
 */
export const getRooms = async () => {
	try {
		const url = buildApiUrl('roomBooking', '/rooms');
		const response = await axios.get(url, {
			headers: {
				'Content-Type': 'application/json'
			}
		});
		
		if (response.data && response.data.success) {
			return response.data.data;
		} else {
			throw new Error(response.data?.message || 'Fehler beim Laden der Räume');
		}
	} catch (error) {
		if (error.response) {
			const errorMessage = error.response.data?.message || `HTTP Error ${error.response.status}`;
			throw new Error(errorMessage);
		} else if (error.request) {
			throw new Error('Netzwerkfehler: Keine Antwort vom Server');
		} else {
			throw new Error(error.message || 'Unbekannter Fehler');
		}
	}
};

/**
 * TanStack Query configuration for searching logs
 * @param {Object} filters - Search filters
 * @returns {Object} Query configuration object
 */
export const useLogsSearchQuery = (filters = {}) => {
	return {
		queryKey: queryKeys.adminLogs(filters),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error('Query was cancelled');
			}
			return searchLogs(filters);
		},
		staleTime: 1000 * 60 * 2, // 2 minutes - logs change frequently
		gcTime: 1000 * 60 * 10, // 10 minutes
		retry: 2, // Less retries for admin queries
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 15000),
		// Only enable if filters are provided or if explicitly enabled
		enabled: Object.keys(filters).length > 0,
	};
};

/**
 * TanStack Query configuration for fetching rooms
 * @returns {Object} Query configuration object
 */
export const useAdminRoomsQuery = () => {
	return {
		queryKey: queryKeys.adminRooms(),
		queryFn: ({ signal }) => {
			if (signal?.aborted) {
				throw new Error('Query was cancelled');
			}
			return getRooms();
		},
		staleTime: 1000 * 60 * 15, // 15 minutes - rooms don't change often
		gcTime: 1000 * 60 * 60, // 1 hour
		retry: 3,
		retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
		enabled: true,
	};
};